import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { configureAuthClient } from '@orborix/auth-client';
import { App } from './App.js';
import { ErrorBoundary } from './components/error-boundary/ErrorBoundary.js';
import { apiBaseUrl, isNative } from './lib/platform.js';
import {
  clearNativeAuthToken,
  loadNativeAuthToken,
  persistNativeAuthToken,
} from './lib/nativeAuth.js';
import { initOtaUpdates } from './lib/native/ota.js';
import { initSentry } from './lib/sentry.js';
import { initAnalytics } from './lib/analytics.js';
import './styles/tokens.css';
import './styles/global.css';

// Sentry (web + native, off without VITE_SENTRY_DSN): first, so the errors of the
// boot are queued; the SDK itself loads after first paint (lib/sentry.ts).
initSentry();

// Product analytics (PostHog, first-party, no consent prompt by design — off without
// VITE_POSTHOG_KEY/HOST): events queue from here; the SDK loads after load + idle.
initAnalytics();

// OTA live updates (native only, no-op on the web and without the plugin): rollback
// report, session tags, and when a downloaded bundle may go live (never in the middle
// of a voice session, a playback, a purchase or a recording — lib/native/ota.ts).
// notifyAppReady() is NOT here: it goes out from App's first commit.
void initOtaUpdates();

// Auth compartido (@orborix/auth-client): identidad de la app para el audit de
// auth + bridge nativo Capacitor (Fase 23). Debe correr antes del primer
// render (el AuthProvider hidrata `me()` al montar).
configureAuthClient({
  appSource: 'speak',
  // Native language every new speak account is born with: speak teaches
  // English to Spanish speakers (the onboarding's country step, mandatory,
  // confirms it). Owner decision 2026-10-09: mandatory at sign-up.
  signupNativeLanguage: 'es',
  native: {
    isNative,
    apiBaseUrl,
    loadToken: loadNativeAuthToken,
    persistToken: persistNativeAuthToken,
    clearToken: clearNativeAuthToken,
  },
});

// Breadcrumb de boot: si una tab se cuelga, esto nos dice hasta dónde llegó.
// En dev Vite reenvía la consola del browser al log del dev server (PM2), así
// que el diagnóstico queda server-side sin pedirle nada al usuario.
console.info('[boot] speak main.tsx eval', { mode: import.meta.env.MODE });

// Helpers DEV-only del motor A0 en `window.__orborixDev` (debug). Import dinámico
// tras el gate `import.meta.env.DEV` → NO entra al bundle de prod. Ver
// `screens/a0/debug/devGlobals.ts` + docs/architecture/09-speak.md.
if (import.meta.env.DEV) {
  void import('./screens/a0/debug/devGlobals.js');
}

// Version skew tras un deploy: una tab vieja pide un chunk lazy con hash que
// ya no existe → el import dinámico falla. Recargamos UNA vez (flag de sesión
// para no loopear) para tomar el bundle nuevo. Hoy speak no tiene imports
// dinámicos (bundle único), pero esto nos cubre apenas alguien agregue uno.
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem('chunk-reloaded')) {
    console.error('[boot] vite:preloadError repetido — no recargo de nuevo', event);
    return;
  }
  sessionStorage.setItem('chunk-reloaded', '1');
  console.warn('[boot] chunk inexistente (deploy en el medio) — recargando una vez');
  window.location.reload();
});

// Dev kill-switch: si alguna vez se sirvió speak como build prod (que registra
// un SW vía vite-plugin-pwa), ese SW queda activo en el browser y SIGUE
// interceptando `/api/*` cuando volvés a dev — devolviendo 404 para endpoints
// nuevos que su precache no conoce. Lo desregistramos en dev para evitar ese
// caso. EXCEPCIÓN: el modo `pwadev` (`dev:pwa`) prende el SW de dev a
// propósito para probar la PWA; ahí NO lo matamos.
if (
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'pwadev' &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator
) {
  navigator.serviceWorker
    .getRegistrations()
    .then(async (regs) => {
      if (regs.length === 0) return;
      console.info('[boot] dev SW cleanup: desregistrando service workers', {
        registrations: regs.length,
      });
      for (const r of regs) await r.unregister();
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // El SW se desregistró, pero ESTA página sigue controlada por él hasta una
      // recarga (sus fetch de `/api/*` quedan colgados → "provisional headers").
      // Si todavía hay un SW controlando, recargamos UNA vez (flag para no loopear)
      // para que la página cargue limpia y las rutas de API respondan.
      if (navigator.serviceWorker.controller && !sessionStorage.getItem('sw-cleaned')) {
        sessionStorage.setItem('sw-cleaned', '1');
        console.warn('[dev] Removed stale ServiceWorker + caches. Reloading once for fresh API routes.');
        window.location.reload();
      }
    })
    .catch(() => {
      /* ignore */
    });
}

// PROD: auto-reload cuando se activa un Service Worker nuevo (deploy nuevo).
// `registerType: 'autoUpdate'` instala el SW nuevo pero NO recarga la página → el
// primer load tras un deploy corre el bundle VIEJO del precache (bug clásico: un
// fetch con credentials a un CDN que responde Allow-Origin '*'). Al activarse el SW
// nuevo, `clientsClaim` dispara `controllerchange` en esta tab → recargamos para
// servir el bundle nuevo. Sin esto haría falta un hard-reload manual.
// Gateo: si hay una sesión de voz EN VIVO, diferimos la recarga hasta que termina
// (`voice-session-ended`, emitido por useConversationController) para no cortarla.
// `!isNative`: en el wrapper Capacitor el SW está deshabilitado en el build (ver
// vite.config.ts `disable: mode==='native'`), así que `controllerchange` nunca
// dispara — no enganchamos el listener para no dejar nada colgado (Fase 23 §3.4).
if (
  !import.meta.env.DEV &&
  !isNative &&
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator
) {
  let reloading = false;
  const reloadOnce = (): void => {
    if (reloading) return;
    reloading = true;
    console.info('[boot] SW nuevo activo (deploy) — recargando para tomar el bundle nuevo');
    window.location.reload();
  };
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (window.__voiceSessionActive) {
      window.__swReloadPending = true;
      return;
    }
    reloadOnce();
  });
  window.addEventListener('voice-session-ended', () => {
    if (window.__swReloadPending) reloadOnce();
  });
}

// Guardrail: el service worker SÓLO se registra en contexto seguro
// (localhost / 127.0.0.1 / HTTPS). Si abrís `pwadev` por una IP de LAN o
// Tailscale en http://, el browser lo bloquea en silencio — avisamos por qué.
if (
  import.meta.env.MODE === 'pwadev' &&
  typeof window !== 'undefined' &&
  !window.isSecureContext
) {
  console.warn(
    '[pwadev] Contexto inseguro: el Service Worker NO se va a registrar. ' +
      'Abrí http://localhost:9003 (no una IP de LAN/Tailscale por http://). ' +
      'En celular necesitás HTTPS (Tailscale Serve).',
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
